import { ADMIN_DOMAIN_NAMES } from '../constants';
import { User } from '../models/user.model';
import { nameMatchTrim } from './string.helpers';
import { Project } from '../models/project.model';
import { Recipe } from '../models/recipe.model';
import { IProduct } from '../models/product.model';
import { omit, omitUndefined } from './object.helpers';
import shallowEqual from './array.helpers';
// import { isLockedOrArchived } from './project.helpers';
import { isObject, sortBy } from 'lodash';

/** Profile label shown in admin UIs (nickname preferred over email-style `name`). */
export const displayNameFromUser = (
  user: Pick<User, 'nickname' | 'name'>,
): string | null => user.nickname || user.name || null;

/**
 * @returns true if the user's email domain is in the list of admin domains
 */
export const isAdmin = (user?: User): boolean =>
  ADMIN_DOMAIN_NAMES.some((domain) => user?.name.includes(domain));

/**
 * @returns true if the user is a member of an admin group
 */
export const isAdminGroupMember = (user?: User): boolean => {
  if (!user) {
    return false;
  }
  return user?.organizations.some((org) =>
    ADMIN_DOMAIN_NAMES.includes(nameMatchTrim(org.name)),
  );
};

export const getUserOrganizationNames = (user: User): string[] => {
  return user.organizations.map((org) => org.name);
};

/**
 * Test if a user can write to a resource.
 * @param uid - The user's ID
 * @param organization - The organization user belongs to
 * @param resource - Project, Recipe, or Product to check
 * @param changes - If updating lock/archived, the new values will be required to verify changes
 * @returns
 */
export const canWrite = <T extends Project | Recipe | IProduct>(
  uid: string,
  organization: string,
  resource: T,
  changes: Partial<T> = {},
): boolean => {
  changes = omitUndefined(changes);
  const { organizations = [], owner } = resource;

  const isMember = organizations.includes(organization);
  const isOwner = uid ? uid === owner : false;

  // Never allow changing organizations
  if (isOrganizationsChange(resource, changes)) {
    return false;
  }

  // Owners can write depending on locked/archived status
  if (isOwner) {
    return canOwnerWrite(resource, changes);
  }

  if (isMember) {
    // If owner is changing to current user allow changes as owner
    if (changes.owner === uid) {
      // TODO: Should verify that user is part of organization before switching
      return canOwnerWrite(resource, changes);
    }

    // Members can only make changes if project is not locked or archived
    return !isLockedOrArchived(resource);
  }
  return false;
};

/**
 * Test if the organizations field will be changed
 * @param resource
 * @param changes
 * @returns
 */
const isOrganizationsChange = <T extends Project | Recipe | IProduct>(
  resource: T,
  changes: Partial<T>,
): boolean => {
  if (changes.organizations) {
    return !shallowEqual(
      sortBy(changes.organizations),
      sortBy(resource.organizations || []),
    );
  }
  return false;
};

/**
 * Test if the owner can makes changes to a resource depending on locked/archived status.
 * @param resource
 * @param changes
 */

const canOwnerWrite = <T extends Project | Recipe | IProduct>(
  resource: T,
  changes: Partial<T> = {},
) => {
  const locked = isLocked(resource);
  const archived = isArchived(resource);
  const archivedAfterChanges = isArchived({ ...resource, ...changes });

  // Any changes can be made if resource WAS unarchived or WILL BE after changes
  if (locked || !archived || !archivedAfterChanges) {
    return true;
  }
  // Undefined values will be ignored by omitUndefined
  changes = omitUndefined(changes);

  const otherChanges = omit(
    changes as Partial<Project>,
    'locked',
    'archived',
    'owner',
    'id',
  );

  // Don't allow write if no changes is provided to verify or if there are other changes
  if (
    Object.keys(changes).length === 0 ||
    Object.keys(otherChanges).length > 0
  ) {
    return false;
  }

  // If still locked or archived, only allow changes to be made to the locked/archived fields
  return 'archived' in changes || 'locked' in changes;
};

export const canDeleteResource = (
  uid: string,
  organization: string,
  resource: Project | Recipe | IProduct,
): boolean => {
  const { owner, organizations = [] } = resource;
  const isMember = organizations.includes(organization);
  const isOwner = uid ? uid === owner : false;
  const locked = isLocked(resource);

  if (isOwner) return true;
  if (isMember) return locked ? false : true;
  return false;
};

export const isLocked = (resource: Project | Recipe | IProduct): boolean =>
  isObject(resource) && 'locked' in resource && !!resource.locked;

export const isArchived = (resource: Project | Recipe | IProduct): boolean =>
  isObject(resource) && 'archived' in resource && !!resource.archived;

export const isLockedOrArchived = (
  resource: Project | Recipe | IProduct,
): boolean => isLocked(resource) || isArchived(resource);
